<template>
  <b-modal
    id="my-timesheet-history-modal"
    modal-class="timesheet-modal"
    title="Timesheet History"
    size="lg"
    hide-footer
    @shown="load"
  >
    <div class="d-flex justify-content-end mb-3">
      <b-button variant="primary" size="sm" @click="openAdd">
        + Add Entry
      </b-button>
    </div>

    <div class="filter-row d-flex flex-wrap mb-3">
      <b-form-select
        v-model="filters.projectId"
        :options="projectFilterOptions"
        size="sm"
        class="mr-2 mb-2 filter-field"
      />
      <b-form-select
        v-model="filters.status"
        :options="statusFilterOptions"
        size="sm"
        class="mr-2 mb-2 filter-field"
      />
      <b-form-datepicker
        v-model="filters.from"
        placeholder="From date"
        size="sm"
        class="mr-2 mb-2 filter-field"
      />
      <b-form-datepicker
        v-model="filters.to"
        placeholder="To date"
        size="sm"
        class="mb-2 filter-field"
      />
    </div>

    <b-alert
      :show="!!error"
      variant="danger"
      dismissible
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <b-spinner v-if="loading" small class="d-block mx-auto my-4" />

    <div v-else-if="!groupedEntries.length" class="text-muted text-center py-4">
      No timesheet entries found.
    </div>

    <div v-else>
      <div
        v-for="group in groupedEntries"
        :key="group.date"
        class="date-group mb-4"
      >
        <div class="date-group-heading">{{ group.date }}</div>

        <div v-for="entry in group.entries" :key="entry.id" class="entry-card">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="font-weight-bold">
                {{ entry.project && entry.project.name }}
              </div>
              <div class="text-muted small">
                {{
                  formatBusinessTime(
                    entry.clocked_in_at,
                    undefined,
                    entry.timezone,
                  )
                }}
                -
                {{
                  formatBusinessTime(
                    entry.clocked_out_at,
                    undefined,
                    entry.timezone,
                  )
                }}
                <span v-if="timezoneLabel(entry.timezone)">{{
                  timezoneLabel(entry.timezone)
                }}</span>
                <span v-if="workedLabel(entry)">
                  · {{ workedLabel(entry) }}</span
                >
                <span v-if="isOvernight(entry)" class="text-muted">
                  · Overnight</span
                >
              </div>
              <div v-if="entry.supervisor" class="text-muted small">
                Sent to: {{ entry.supervisor.name }}
              </div>
            </div>
            <div class="d-flex align-items-start">
              <span class="status-pill mr-2" :class="eventStatusClass(entry)">
                {{ eventStatusLabel(entry) }}
              </span>
              <b-dropdown
                v-if="entry.status !== 'approved'"
                variant="link"
                size="sm"
                right
                no-caret
                toggle-class="p-0 text-muted"
              >
                <template #button-content>
                  <b-icon icon="three-dots" />
                </template>
                <b-dropdown-item @click="openEdit(entry)">Edit</b-dropdown-item>
                <b-dropdown-item
                  v-if="entry.status !== 'rejected'"
                  variant="danger"
                  @click="confirmDelete(entry)"
                >
                  Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div
            v-if="entry.status === 'rejected' && entry.admin_comment"
            class="rejection-reason mt-2"
          >
            {{ entry.admin_comment }}
          </div>

          <b-button
            v-if="entry.status === 'rejected'"
            variant="primary"
            size="sm"
            block
            class="mt-2"
            @click="openEdit(entry)"
          >
            Edit &amp; Resubmit
          </b-button>

          <div v-if="entry.comment" class="mt-2 small">{{ entry.comment }}</div>

          <div
            v-if="entry.plant_entries && entry.plant_entries.length"
            class="mt-2 small text-muted"
          >
            {{ plantEntriesLabel(entry.plant_entries) }}
          </div>

          <div
            v-if="entry.photo_urls && entry.photo_urls.length"
            class="photo-thumbs mt-2"
          >
            <a
              v-for="photo in entry.photo_urls"
              :key="photo.id"
              :href="photo.url"
              target="_blank"
              rel="noopener"
            >
              <b-img :src="photo.url" class="photo-thumb" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <tradie-edit-timesheet-dialog
      :existing="editingEntry"
      :projects="projects"
      @saved="onSaved"
    />
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import {
  formatBusinessTime,
  timezoneLabel,
  eventStatusLabel,
  eventStatusClass,
  eventMinutes,
  formatHours,
  bizFromIso,
} from '@/utils/timesheetHelpers'
import TradieEditTimesheetDialog from './TradieEditTimesheetDialog'

// Prefer the API's own site_date (computed once, in the row's own stamped
// timezone — see docs/designs/per-site-timezones.md) over recomputing it
// from clocked_in_at here. Falls back for an event that predates the
// site_date column.
function siteDateOf(e) {
  return e.site_date
    ? dayjs(e.site_date)
    : bizFromIso(e.clocked_in_at, e.timezone)
}

export default {
  name: 'TimesheetHistoryModal',

  components: { TradieEditTimesheetDialog },

  props: {
    projects: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading: false,
      entries: [],
      editingEntry: null,
      error: null,
      filters: {
        projectId: null,
        status: null,
        from: null,
        to: null,
      },
    }
  },

  computed: {
    projectFilterOptions() {
      return [
        { value: null, text: 'All Projects' },
        ...this.projects.map((p) => ({ value: p.id, text: p.name })),
      ]
    },

    statusFilterOptions() {
      return [
        { value: null, text: 'All Statuses' },
        { value: 'clocked_out', text: 'Pending' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ]
    },

    filteredEntries() {
      return this.entries.filter((e) => {
        if (this.filters.projectId && e.project_id !== this.filters.projectId) {
          return false
        }
        if (this.filters.status && e.status !== this.filters.status) {
          return false
        }
        if (this.filters.from || this.filters.to) {
          const day = siteDateOf(e).format('YYYY-MM-DD')
          if (this.filters.from && day < this.filters.from) return false
          if (this.filters.to && day > this.filters.to) return false
        }
        return true
      })
    },

    groupedEntries() {
      const groups = {}
      this.filteredEntries.forEach((e) => {
        const day = siteDateOf(e).format('D MMM YYYY')
        if (!groups[day]) groups[day] = []
        groups[day].push(e)
      })
      return Object.keys(groups).map((date) => ({
        date,
        entries: groups[date],
      }))
    },
  },

  methods: {
    ...mapActions('timesheet', ['getMyTimesheetHistory', 'myDeleteEvent']),
    formatBusinessTime,
    timezoneLabel,
    eventStatusLabel,
    eventStatusClass,

    async load() {
      this.loading = true
      try {
        this.entries = await this.getMyTimesheetHistory()
      } finally {
        this.loading = false
      }
    },

    openAdd() {
      this.editingEntry = null
      this.$nextTick(() => this.$bvModal.show('tradie-edit-timesheet-modal'))
    },

    openEdit(entry) {
      this.editingEntry = entry
      this.$nextTick(() => this.$bvModal.show('tradie-edit-timesheet-modal'))
    },

    async onSaved() {
      await this.load()
    },

    async confirmDelete(entry) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This will permanently remove this timesheet entry.',
        {
          title: 'Delete Entry',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
        },
      )
      if (!confirmed) return

      try {
        await this.myDeleteEvent(entry.id)
        await this.load()
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          'Unable to delete this entry.'
      }
    },

    isOvernight(entry) {
      if (!entry.clocked_in_at || !entry.clocked_out_at) return false
      const inDay = bizFromIso(entry.clocked_in_at, entry.timezone)
      const outDay = bizFromIso(entry.clocked_out_at, entry.timezone)
      return outDay.format('YYYY-MM-DD') > inDay.format('YYYY-MM-DD')
    },

    workedLabel(entry) {
      const minutes = eventMinutes(
        entry.clocked_in_at,
        entry.clocked_out_at,
        entry.break_minutes,
      )
      return formatHours(minutes)
    },

    plantEntriesLabel(plantEntries) {
      const unit = { hourly: 'Hr', day: 'Day' }
      return plantEntries
        .map((pe) =>
          [
            `${pe.plant_name || pe.name || 'Equipment'} x${pe.quantity}`,
            unit[pe.rate_type],
          ]
            .filter(Boolean)
            .join(' '),
        )
        .join(', ')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.filter-field {
  width: auto;
  min-width: 140px;
}

.date-group-heading {
  font-weight: 600;
  margin-bottom: 8px;
  color: #6b7280;
}

.entry-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.rejection-reason {
  background: #fef2f2;
  color: #991b1b;
  padding: 8px;
  border-radius: 6px;
  font-size: 13px;
}

.photo-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.photo-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
